import React from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { initSentry } from './lib/sentry'

// Sprint I-3: Sentry init MUST run before App/Router so any boot-time
// crash (i18n init, query-client setup) is captured. No-op when
// VITE_SENTRY_DSN is unset (dev + demo without DSN).
initSentry()

import './i18n'  // i18n must be imported before App to initialize translations
import App from './App'
import './theme.css'  // global theme variables — must load before any lazy page CSS
import './index.css'
import './styles/cream-page.css'  // shared editorial cream scope (.mashfa-cream)
import './styles/print.css'  // shared @media print rules — hides app chrome (banner, FAB, sidebar toggle) from printouts
import './styles/form.css'  // reusable modal-form design tokens (--form-*) + .app-form helpers

// ── Phase 1.5: deploy-cache-recovery hardening ─────────────────────────
//
// Three safety nets layered against the "old browser tab + new server bundle"
// failure mode (see known_issues.md "Frontend-Deploys brechen aktive Browser-
// Sessions"). Order matters — register SW BEFORE any lazy-import happens so a
// dynamic-import crash can't bypass the kill-switch:
//
//   1. SW-register synchronous-on-module-eval (before createRoot)
//   2. Listen for sw.js "force-reload" postMessage as a fallback when the SW's
//      own client.navigate() failed silently (cross-origin / detached / bfcache)
//   3. Global error + unhandledrejection handler: when a chunk-load fails with
//      "Failed to fetch dynamically imported module" we reload exactly ONCE per
//      session (sessionStorage flag prevents a reload loop)

const RELOAD_FLAG = 'mashfa.cache-recovery-reload'

function selfHealOnChunkLoadError(reason: unknown) {
  const msg = reason instanceof Error ? reason.message : String(reason)
  const isChunkError = /Failed to fetch dynamically imported module|Loading chunk \d+ failed|Importing a module script failed/.test(msg)
  if (!isChunkError) return
  if (sessionStorage.getItem(RELOAD_FLAG)) return  // already reloaded this session — avoid loop
  sessionStorage.setItem(RELOAD_FLAG, '1')
  // eslint-disable-next-line no-console
  console.warn('[mashfa] chunk-load failed — self-healing reload:', msg)
  globalThis.location.reload()
}

globalThis.addEventListener('error', (e) => selfHealOnChunkLoadError(e.error ?? e.message))
globalThis.addEventListener('unhandledrejection', (e) => selfHealOnChunkLoadError(e.reason))

if ((import.meta as { env?: { PROD?: boolean } }).env?.PROD && 'serviceWorker' in navigator) {
  // Register synchronously before lazy chunks can fail. Old `window.load`
  // listener was too late — App.tsx imports trigger chunk fetches before load.
  navigator.serviceWorker.register('/sw.js').catch(() => {
    // SW registration failed — app still works without it
  })
  // Fallback path for when sw.js's client.navigate(url) didn't reach this tab:
  // sw.js posts {type:'mashfa-force-reload'} to all clients during cleanup.
  navigator.serviceWorker.addEventListener('message', (e) => {
    if (e.data?.type === 'mashfa-force-reload') {
      if (sessionStorage.getItem(RELOAD_FLAG)) return
      sessionStorage.setItem(RELOAD_FLAG, '1')
      globalThis.location.reload()
    }
  })
}

const root = createRoot(document.getElementById('root')!)

root.render(
  <React.StrictMode>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </React.StrictMode>
)
